import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItemButton,
  ListItemAvatar, Avatar, ListItemText, Checkbox, CircularProgress, Box, Typography,
  TextField, InputAdornment
} from '@mui/material';
import CallIcon from '@mui/icons-material/Call';
import SearchIcon from '@mui/icons-material/Search';
import useApi from 'hooks/useApi';
import useInitiateCall from 'hooks/call-queue/useInitiateCall';
import { useOutgoingCall } from 'contexts/OutgoingCallContext';

interface Candidate {
  uid: string;
  name: string;
  firstName?: string;
  lastName?: string;
  role?: string;
}

const getInitials = (name: string) => {
  const w = (name || '').trim().split(/\s+/).slice(0, 2);
  if (w.length === 0 || !w[0]) return 'U';
  if (w.length === 1) return w[0].slice(0, 2).toUpperCase();
  return (w[0][0] + w[1][0]).toUpperCase();
};

/**
 * Globally-mounted "pick who to call" dialog for dashboard → app calls. Opened by
 * any Call button via OutgoingCallContext.openCallDialog(transactionUid). Lists
 * the transaction's callable users, supports single or multi-select, and on
 * "Start Call" initiates the call and navigates into the in-session call UI.
 *
 * Quick Call: an opener may pass `restrictToUids` to narrow the roster to a
 * subset — the transaction Details page passes the users on that transaction
 * (assigned via a step's RESOURCE field, or having submitted a step), so its Call
 * button lands straight on them. The rest of the team stays one "Show all users"
 * click away. Openers that pass nothing (e.g. the chat window) get the unchanged
 * full-team list.
 */
const CallUserDialog = () => {
  const navigate = useNavigate();
  const { getCallCandidates, showToast } = useApi();
  const { initiateCall } = useInitiateCall();
  const { callDialogTxn, callDialogRestrictToUids, closeCallDialog } = useOutgoingCall();

  const open = !!callDialogTxn;
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  // Quick Call escape hatch: when the opener restricted the roster, this lets the
  // caller fall back to the full team without leaving the dialog.
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!open || !callDialogTxn) return;
    let cancelled = false;
    setLoading(true);
    setSelected([]);
    setSearch('');
    setShowAll(false);
    getCallCandidates(callDialogTxn)
      .then((res: any) => {
        if (cancelled) return;
        if (res?.success) {
          setCandidates(Array.isArray(res.data) ? res.data : []);
        } else {
          setCandidates([]);
          showToast?.(res?.message || 'Could not load users', 'error');
        }
      })
      .catch(() => { if (!cancelled) setCandidates([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, callDialogTxn, getCallCandidates, showToast]);

  const toggle = (uid: string) =>
    setSelected((prev) => (prev.includes(uid) ? prev.filter((u) => u !== uid) : [...prev, uid]));

  const handleStart = async () => {
    if (!callDialogTxn || selected.length === 0) return;
    setStarting(true);
    const callId = await initiateCall(callDialogTxn, { targetUserUids: selected });
    setStarting(false);
    if (callId) {
      closeCallDialog();
      navigate(`/session/${callId}`);
    }
  };

  // Quick Call: when the opener passed a restriction (the Details page passes the
  // users on that transaction — assigned on a step, or having submitted one), the
  // roster is narrowed to them. Any candidate not in that set is hidden until the
  // caller asks for everyone.
  const restrictSet =
    callDialogRestrictToUids && callDialogRestrictToUids.length > 0
      ? new Set(callDialogRestrictToUids.map(String))
      : null;
  const restricting = !!restrictSet && !showAll;
  const scoped = restricting ? candidates.filter((c) => restrictSet!.has(String(c.uid))) : candidates;
  const hiddenCount = restricting ? candidates.length - scoped.length : 0;

  const q = search.toLowerCase();
  const filtered = scoped.filter(
    (c) => !q || c.name.toLowerCase().includes(q) || (c.role || '').toLowerCase().includes(q)
  );

  return (
    <Dialog open={open} onClose={() => !starting && closeCallDialog()} fullWidth maxWidth="xs">
      <DialogTitle>Start a call</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: 2, pb: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search users…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18 }} />
                </InputAdornment>
              )
            }}
          />
        </Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ py: 4, px: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {restricting
                ? 'No users on this transaction are available to call.'
                : 'No users available to call for this transaction.'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 320, overflowY: 'auto', py: 0 }}>
            {filtered.map((c) => (
              <ListItemButton key={c.uid} onClick={() => toggle(c.uid)} dense>
                <Checkbox edge="start" checked={selected.includes(c.uid)} tabIndex={-1} disableRipple />
                <ListItemAvatar>
                  <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.light', color: 'primary.main', fontSize: 13 }}>
                    {getInitials(c.name)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={c.name} secondary={c.role || undefined} />
              </ListItemButton>
            ))}
          </List>
        )}
        {!loading && restricting && hiddenCount > 0 && (
          <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
            <Button size="small" onClick={() => setShowAll(true)}>
              Show all users ({hiddenCount} more)
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {selected.length > 0 ? `${selected.length} selected` : ''}
        </Typography>
        <Button onClick={() => closeCallDialog()} disabled={starting} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleStart}
          disabled={selected.length === 0 || starting}
          variant="contained"
          startIcon={starting ? <CircularProgress size={16} color="inherit" /> : <CallIcon sx={{ fontSize: 16 }} />}
        >
          {starting ? 'Starting…' : 'Start Call'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CallUserDialog;
